import { forwardRef } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * Drawer — Radix Dialog asosida slide-in yon panel.
 * Faza 3 use-case'lar:
 *   - DealDetailDrawer (deal detail ko'rinishi)
 *   - LeadDetailDrawer (Sprint 3)
 *
 * Accessibility:
 *   - Radix focus trap, Esc, aria-labelledby/describedby
 *   - DrawerTitle MAJBURIY (a11y)
 *   - Right slide-in 250ms spring (motion-safe)
 */
export const Drawer = DialogPrimitive.Root;
export const DrawerTrigger = DialogPrimitive.Trigger;
export const DrawerClose = DialogPrimitive.Close;
export const DrawerPortal = DialogPrimitive.Portal;

export const DrawerOverlay = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[var(--z-overlay)] bg-[var(--bg-overlay)] backdrop-blur-sm",
      "data-[state=open]:animate-in data-[state=open]:fade-in-0",
      "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
      className,
    )}
    {...props}
  />
));
DrawerOverlay.displayName = "DrawerOverlay";

const sizeMap: Record<NonNullable<DrawerContentProps["size"]>, string> = {
  sm: "w-[380px]",
  md: "w-[480px]",
  lg: "w-[640px]",
  xl: "w-[800px]",
};

const sideMap: Record<NonNullable<DrawerContentProps["side"]>, string> = {
  right:
    "right-0 top-0 bottom-0 border-l border-[var(--border-strong)] " +
    "data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right",
  left:
    "left-0 top-0 bottom-0 border-r border-[var(--border-strong)] " +
    "data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left",
};

export interface DrawerContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /** Drawer kengligi: sm/md/lg/xl */
  size?: "sm" | "md" | "lg" | "xl";
  /** Qaysi tomondan ochiladi */
  side?: "right" | "left";
  /** Default close (X) tugmasini yashirish */
  hideClose?: boolean;
}

export const DrawerContent = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DrawerContentProps
>(({ className, children, size = "md", side = "right", hideClose = false, ...props }, ref) => (
  <DrawerPortal>
    <DrawerOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed z-[var(--z-modal)] flex flex-col",
        "bg-[var(--surface-2)] shadow-[var(--shadow-modal)]",
        "focus:outline-none",
        "data-[state=open]:animate-in data-[state=open]:duration-250",
        "data-[state=closed]:animate-out data-[state=closed]:duration-200",
        "max-w-[100vw]",
        sizeMap[size],
        sideMap[side],
        className,
      )}
      {...props}
    >
      {children}
      {!hideClose && (
        <DialogPrimitive.Close
          aria-label="Yopish"
          className={cn(
            "absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-md",
            "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface)]",
            "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-violet)]/60",
          )}
        >
          <X className="h-4 w-4" aria-hidden />
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DrawerPortal>
));
DrawerContent.displayName = "DrawerContent";

export const DrawerHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "sticky top-0 z-10 flex flex-col gap-1.5 border-b border-[var(--border-subtle)] bg-[var(--surface-2)] px-6 py-4",
      className,
    )}
    {...props}
  />
);
DrawerHeader.displayName = "DrawerHeader";

export const DrawerActions = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("absolute right-12 top-3 flex items-center gap-1", className)}
    {...props}
  />
);
DrawerActions.displayName = "DrawerActions";

export const DrawerBody = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex-1 overflow-y-auto px-6 py-4", className)} {...props} />
);
DrawerBody.displayName = "DrawerBody";

export const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "sticky bottom-0 z-10 flex items-center justify-end gap-2 border-t border-[var(--border-subtle)] bg-[var(--surface-2)] px-6 py-3",
      className,
    )}
    {...props}
  />
);
DrawerFooter.displayName = "DrawerFooter";

export const DrawerTitle = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-base font-semibold tracking-tight text-[var(--text-primary)]",
      className,
    )}
    {...props}
  />
));
DrawerTitle.displayName = "DrawerTitle";

export const DrawerDescription = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-[var(--text-secondary)]", className)}
    {...props}
  />
));
DrawerDescription.displayName = "DrawerDescription";
